import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../utils/constants";
import { useAuth } from "../context/AuthContext";
import { getUserProfile } from "../api/services/profileService";
import { logoutUser } from "../api/services/authService";
import { getLoginPageByRole } from "../utils/authHelpers";
import { hasPermission } from "../utils/permissions";
import {
  Search,
  User,
  LogOut,
  Settings,
  Bell,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";
import LanguageToggle from "../components/LanguageToggle";
import CyberBrand from "../components/common/CyberBrand";

function Navbar({
  showNavLinks = true,
  showSearch = true,
  showUserMenu = true,
  showLanguageToggle = true,
  variant = "default", // "default" | "minimal"
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { logout, user } = useAuth();
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { i18n, t } = useTranslation();
  const isRTL = i18n.language === "ar";

  const isMinimal = variant === "minimal";
  const displayNavLinks = isMinimal ? false : showNavLinks;
  const displaySearch = isMinimal ? false : showSearch;
  const displayUserMenu = isMinimal ? false : showUserMenu;
  const displayLanguageToggle = showLanguageToggle;
  const showRightControls =
    displaySearch || displayUserMenu || displayLanguageToggle;
  const hasMobileMenu = displayNavLinks || displayUserMenu || displaySearch;

  const isActive = (path, label) => {
    if (label === "Dashboard")
      return location.pathname === "/home" || location.pathname === "/";
    if (label === "Topology") return location.pathname === "/topology";
    if (label === "Boxes")
      return (
        location.pathname.startsWith("/boxes") ||
        location.pathname.startsWith("/lab")
      );
    return false;
  };

  const normalizedRoles = (user?.roles || []).map((role) => role.toLowerCase());
  const isStudent = normalizedRoles.includes("student");
  const canViewBoxes = hasPermission(user, "catalog_view");
  const canViewTopology = hasPermission(user, "topology_access") && !isStudent;
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };

  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  const handleSearchClick = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log("Searching:", searchQuery);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setIsLoggingOut(true);
    try {
      await logoutUser();
      const loginPage = getLoginPageByRole(user?.roles);
      logout();
      navigate(loginPage, { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleProfile = async () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    try {
      const profileData = await getUserProfile();
      console.log("User profile data:", profileData);
      navigate("/profile");
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      navigate("/profile");
    }
  };

  const navItems = [
    { path: ROUTES.HOME, label: "Dashboard" },
    ...(canViewBoxes ? [{ path: "/boxes", label: "Boxes" }] : []),
    ...(canViewTopology ? [{ path: "/topology", label: "Topology" }] : []),
  ];

  // Compute display role label
  const displayRoleLabel = (() => {
    if (!user?.roles || user.roles.length === 0) return "User";
    const roleLabelMap = {
      platform_admin: "Platform Admin",
      company_admin: "Company Admin",
      company_user: "Company User",
      student: "Student",
      "default-roles-qv-platform": "Professional",
    };
    const priorityRoles = [
      "platform_admin",
      "company_admin",
      "company_user",
      "student",
      "default-roles-qv-platform",
    ];
    const normalizedRoles = user.roles.map((r) => r.toLowerCase());
    const displayRole =
      priorityRoles.find((r) => normalizedRoles.includes(r)) ||
      normalizedRoles[0];
    return (
      roleLabelMap[displayRole] ||
      displayRole.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    );
  })();

  return (
    <>
      <nav
        dir="ltr"
        className={`sticky top-0 z-30 transition-all duration-300 border-b bg-(--color-card) backdrop-blur-2xl border-slate-200 dark:border-white/5 ${isScrolled && !isMinimal ? "shadow-sm dark:shadow-[0_4px_30px_rgba(0,0,0,0.5)]" : ""}`}
      >
        <div className="mx-auto flex max-w-455 items-center justify-between gap-6 px-4 md:px-10 py-2 min-h-19">
          {/* ── Logo + Nav links grouped on the left ── */}
          <div className="flex items-center gap-8">
            <Link
              to="/home"
              className="flex items-center transition-colors hover:opacity-90"
            >
              <CyberBrand size="md" showSubtitle={false} />
            </Link>

            {/* ── Desktop: nav links ── */}
            {displayNavLinks && (
              <ul
                className="hidden md:flex items-center gap-6 text-sm font-medium"
                dir={isRTL ? "rtl" : "ltr"}
              >
                {navItems.map((item, index) => {
                  const active = isActive(item.path, item.label);
                  return (
                    <li key={`${item.path}-${index}`}>
                      <Link
                        to={item.path}
                        className={`relative pb-1 transition-colors text-(--color-text-muted) hover:text-(--color-text-primary) dark:text-(--color-text-primary) ${
                          active ? "text-(--color-text-primary)" : ""
                        }`}
                      >
                        {t(`navbar.links.${item.label.toLowerCase()}`)}
                        {active && (
                          <span className="absolute bottom-0 left-0 w-full h-0.5 bg-(--color-primary) rounded-full" />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ── Desktop: right-side controls ── */}
          {showRightControls && (
            <div
              className={`${isMinimal ? "flex" : "hidden md:flex"} items-center gap-3`}
            >
              {/* Search */}
              {displaySearch && (
                <div ref={searchRef} className="relative hidden lg:block">
                  <form onSubmit={handleSearchSubmit}>
                    <div
                      className={`flex items-center rounded-lg border overflow-hidden transition-[width,background-color,border-color,box-shadow] duration-300 ease-in-out ${
                        isSearchExpanded
                          ? "w-72 bg-(--color-bg-card) border-(--color-border) shadow-lg"
                          : "w-10 bg-(--color-bg-card) border-(--color-border) hover:border-(--color-primary)/50 hover:bg-(--color-bg-card)"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={handleSearchClick}
                        className="shrink-0 flex items-center justify-center w-10 h-10 text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors"
                        aria-label={t("navbar.search_aria")}
                      >
                        <Search className="h-4 w-4" />
                      </button>
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("navbar.search_placeholder")}
                        className={`flex-1 bg-transparent outline-none text-sm text-(--color-text-primary) placeholder-(--color-text-muted) transition-opacity duration-200 ${
                          isSearchExpanded
                            ? "w-full pr-3 opacity-100"
                            : "w-0 pr-0 opacity-0"
                        }`}
                      />
                    </div>
                  </form>
                </div>
              )}

              {/* Language Toggle */}
              {displayLanguageToggle && (
                <LanguageToggle
                  lang={i18n.language}
                  toggleLanguage={toggleLanguage}
                  standalone={false}
                />
              )}

              {/* Notification Bell */}
              {displayUserMenu && (
                <button className="relative p-2 text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors hidden lg:block">
                  <Bell className="h-5 w-5" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                </button>
              )}

              {/* User menu */}
              {displayUserMenu && (
                <div
                  className="relative flex items-center gap-3"
                  ref={dropdownRef}
                >
                  <div
                    className={`hidden sm:block ${i18n.language === "ar" ? "text-left" : "text-right"}`}
                    dir={isRTL ? "rtl" : "ltr"}
                  >
                    <p className="text-sm text-(--color-text-primary) font-medium">
                      {user?.name || t("navbar.user.default_name")}
                    </p>
                    <p className="text-xs text-(--color-text-muted)">
                      {displayRoleLabel}
                    </p>
                  </div>

                  <button
                    onClick={toggleDropdown}
                    aria-label={t("navbar.user_menu_aria")}
                    aria-expanded={isDropdownOpen}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-(--color-primary) font-medium text-sm text-(--color-text-on-primary) transition-all duration-200 hover:opacity-90"
                  >
                    {user?.name?.[0]?.toUpperCase() ||
                      user?.username?.[0]?.toUpperCase() ||
                      "U"}
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute top-full mt-2 w-52 rounded-xl bg-(--color-bg-card) dark:bg-black border border-(--color-border) p-1.5 text-sm shadow-2xl shadow-(--color-shadow-primary)/50 right-0">
                      <div className="px-3 py-2.5 border-b border-(--color-border) mb-1.5">
                        <p className="text-(--color-text-primary) font-medium">
                          {user?.username || "User"}
                        </p>
                        <p className="text-(--color-text-muted) dark:text-gray-400 text-xs">
                          {user?.email || "user@company.com"}
                        </p>
                      </div>
                      <button
                        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-(--color-text-muted) dark:text-gray-300 transition-colors hover:bg-(--color-primary)/10 hover:text-(--color-text-primary) ${i18n.language === "ar" ? "flex-row-reverse text-right" : ""}`}
                        onClick={handleProfile}
                      >
                        <User className="h-4 w-4 text-(--color-text-muted) dark:text-gray-400" />
                        {t("navbar.user.profile")}
                      </button>
                      <button
                        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-(--color-text-muted) dark:text-gray-300 transition-colors hover:bg-(--color-primary)/10 hover:text-(--color-text-primary) ${i18n.language === "ar" ? "flex-row-reverse text-right" : ""}`}
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <Settings className="h-4 w-4 text-(--color-text-muted) dark:text-gray-400" />
                        {t("navbar.user.settings")}
                      </button>
                      <div className="my-1.5 h-px bg-(--color-border)" />
                      <button
                        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300 ${i18n.language === "ar" ? "flex-row-reverse text-right" : ""}`}
                        onClick={handleLogout}
                      >
                        <LogOut className="h-4 w-4" />
                        {t("navbar.user.logout")}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Mobile: hamburger button (always visible on small screens) ── */}
          {hasMobileMenu && (
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg-card) transition-colors"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
      </nav>

      {/* ── Mobile Menu Overlay ── */}
      {hasMobileMenu && isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* ── Mobile Menu Drawer ── */}
      {hasMobileMenu && (
        <div
          ref={mobileMenuRef}
          dir={isRTL ? "rtl" : "ltr"}
          className={`fixed top-0 right-0 h-full w-72 bg-(--color-bg-card) border-l border-(--color-border) z-50 md:hidden flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-(--color-border)">
            <Link
              to="/home"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center transition-colors hover:opacity-90"
            >
              <CyberBrand size="sm" showSubtitle={false} />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg) transition-colors"
              aria-label="Close menu"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* User info strip */}
          {displayUserMenu && (
            <div className="flex items-center gap-3 px-5 py-4 border-b border-(--color-border)">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-(--color-primary) font-medium text-sm text-(--color-text-on-primary)">
                {user?.name?.[0]?.toUpperCase() ||
                  user?.username?.[0]?.toUpperCase() ||
                  "U"}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-(--color-text-primary) truncate">
                  {user?.name ||
                    user?.username ||
                    t("navbar.user.default_name")}
                </p>
                <p className="text-xs text-(--color-text-primary) truncate">
                  {user?.email || "user@company.com"}
                </p>
                <p className="text-xs text-(--color-primary) font-medium">
                  {displayRoleLabel}
                </p>
              </div>
            </div>
          )}

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {/* Nav Links */}
            {displayNavLinks && (
              <div className="px-3 pt-4 pb-2">
                <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-(--color-text-muted)">
                  Navigation
                </p>
                {navItems.map((item, index) => {
                  const active = isActive(item.path, item.label);
                  return (
                    <Link
                      key={`${item.path}-${index}`}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors mb-0.5 ${
                        active
                          ? "bg-(--color-primary)/10 text-(--color-primary)"
                          : "text-(--color-text-primary) hover:bg-(--color-bg)"
                      }`}
                    >
                      <span>
                        {t(`navbar.links.${item.label.toLowerCase()}`)}
                      </span>
                      {active && (
                        <span className="w-1.5 h-1.5 rounded-full bg-(--color-primary)" />
                      )}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Language + Notifications row */}
            {displayUserMenu && (
              <div className="px-3 pt-2 pb-2 border-t border-(--color-border) mt-2">
                <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-(--color-text-muted)">
                  Preferences
                </p>

                {/* Language */}
                <div className="flex items-center justify-between rounded-lg px-3 py-2.5 hover:bg-(--color-bg) transition-colors">
                  <span className="text-sm text-(--color-text-primary)">
                    Language
                  </span>
                  <LanguageToggle
                    lang={i18n.language}
                    toggleLanguage={toggleLanguage}
                    standalone={false}
                  />
                </div>

                {/* Notifications */}
                <button className="flex items-center justify-between w-full rounded-lg px-3 py-2.5 hover:bg-(--color-bg) transition-colors">
                  <span className="text-sm text-(--color-text-primary)">
                    Notifications
                  </span>
                  <div className="relative">
                    <Bell className="h-4 w-4 text-(--color-text-muted)" />
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full" />
                  </div>
                </button>
              </div>
            )}

            {/* Account actions */}
            {displayUserMenu && (
              <div className="px-3 pt-2 pb-4 border-t border-(--color-border) mt-2">
                <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-(--color-text-muted)">
                  Account
                </p>
                <button
                  className="flex items-center justify-between w-full rounded-lg px-3 py-2.5 text-sm text-(--color-text-primary) hover:bg-(--color-bg) transition-colors mb-0.5"
                  onClick={handleProfile}
                >
                  <div className="flex items-center gap-2.5">
                    <User className="h-4 w-4" />
                    {t("navbar.user.profile")}
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                </button>
                <button
                  className="flex items-center justify-between w-full rounded-lg px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors mb-0.5"
                  onClick={handleLogout}
                >
                  <div className="flex items-center gap-2.5">
                    <LogOut className="h-4 w-4" />
                    {t("navbar.user.logout")}
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logout Loader */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-9999">
          <div className="p-8 flex flex-col items-center gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-(--color-primary)/20 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-(--color-primary) rounded-full animate-spin" />
            </div>
            <p className="text-white font-semibold">
              {t("navbar.logging_out")}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
